import PropTypes from 'prop-types';
import React from 'react';
import moment from 'moment';
import { List } from 'immutable';
import { pageContext } from 'ctm-util-functions';
import { getHasSavedResults } from 'ctm-selectors/selectors/results';
import { getFeatureFlag } from 'ctm-selectors/selectors/base';
import { ComparableResultsRow } from '../comparable_results_row/comparable_results_row';
import { ResultsControls } from '../../results_controls/results_controls';
import { QuoteExpiredCard } from '../../../quote_expired_card/quote_expired_card';
import styles from './quick_price.css';

/**
 * A generic modal container that formats, prepares and passes props to our visual modal component
 * @param {Object} props React props
 * @returns {QuickPrice} a QuickPrice component
 */
const QuickPrice = (props) => {
    const { breakpoint, layout, quotes, filteredQuotes, unavailableBrands } = props;

    /**
     * Render the available quotes
     * @param {Immutable.List} quotesList The available, filtered, sorted quotes
     * @param {String} layoutSchema The layout to use
     * @returns {Array} An array of result rows
     */

    const renderQuotes = (quotesList, layoutSchema, hasSavedResults) => {
        const currentDate = moment().format('YYYY-MM-DD');

        return quotesList.map((quote, i) => {
            const productMeta = quote.getIn(['productMeta']);
            const isExpired = moment(currentDate).isAfter(productMeta.get('validUntil'));
            const shouldRenderExpiredCard = isExpired && hasSavedResults;

            if (shouldRenderExpiredCard) {
                const id = `expired-quote-${productMeta.get('id')}`;
                return <QuoteExpiredCard key={id} id={id} quote={quote} />;
            }
            const id = `result-row-${i}`;
            const key = `productId.${productMeta.get('id')}`;
            return (
                <ComparableResultsRow
                    breakpoint={breakpoint}
                    id={id}
                    key={key}
                    layout={layoutSchema}
                    product={quote}
                    index={i}
                />
            );
        });
    };

    /**
     * Render the filtered out quotes if they were filtered out by a filter defined in the components schema
     * @param {Array} filteredOutQuotes - Quotes that were filtered from results
     * @returns {Array} An array of result rows
     */
    const renderFilteredOutQuotes = (filteredOutQuotes) => {
        const context = pageContext.getProps();
        const { models } = context;
        const { componentSchemaModel } = models;
        const vertical = pageContext.getVertical();
        if (componentSchemaModel.componentSchemaExists('layoutFilteredOutResultsRow')) {
            const filteredSchemaModel = componentSchemaModel.getComponentSchema('layoutFilteredOutResultsRow');
            const rows = [];

            filteredOutQuotes.forEach((result, i) => {
                if (filteredSchemaModel.filters[result.filter] && vertical !== 'home-contents-insurance' && vertical !== 'car-insurance') {
                    rows.push(
                        <ComparableResultsRow
                            breakpoint={breakpoint}
                            id={`filtered-product-row-${i}`}
                            key={`filtered-.${result.quote.getIn(['productMeta', 'id'])}`}
                            layout={['component_layoutFilteredOutResultsRow']}
                            product={result.quote}
                            filterSchema={filteredSchemaModel.filters[result.filter]}
                            filter={result.filter}
                            index={i}
                        />
                    );
                }
            });
            return rows;
        }
        return null;
    };

    /**
     * Render the feature filtered out quotes
     * @param {Array} filteredOutQuotes - Quotes that were filtered from results (only H&C feature filteredOutQuotes)
     * @returns {Array} An array of result rows.
     *
     *  HNC: The below filtering is only applied on H&C due to the business requirements.
     *  Unlike the usual rendering of all partners in filtered quotes, now we only render quotes
     *  with an available product, which is determined by looking at quote.productMeta.available
     *
     *  CAR: The below filtering is only applied on CAR's Carpeesh partner due to the business requirements.
     *  Only Capreesh don't provide monthly premium, hence Currently we can not generalise the selection
     *  criteria, and we are checking productMeta.price.monthlyPremium === 0.
     */
    const renderFeatureFilteredOutQuotes = (filteredOutQuotes) => {
        const context = pageContext.getProps();
        const { models } = context;
        const { componentSchemaModel } = models;
        const vertical = pageContext.getVertical();
        if (componentSchemaModel.componentSchemaExists('layoutFilteredOutResultsRow')) {
            const rows = [];
            filteredOutQuotes.forEach((result, i) => {
                if (vertical === 'home-contents-insurance') {
                    if (result.quote.getIn(['productMeta', 'available']) === true) {
                        rows.push(
                            <ComparableResultsRow
                                breakpoint={breakpoint}
                                id={`feature-filtered-product-row-${i}`}
                                key={`FeatureFiltered-.${result.quote.getIn(['productMeta', 'id'])}`}
                                layout={['component_layoutFeatureFilteredOutResultsRow']}
                                product={result.quote}
                            />
                        );
                    }
                }
                if (vertical === 'car-insurance') {
                    const zeroMonthlyPremium = result.quote.getIn(['productMeta', 'price', 'monthlyPremium']) === 0;
                    const notMonthlyAvailable = result.quote.getIn(['productMeta', 'price', 'monthlyAvailable']) === false;
                    if (zeroMonthlyPremium || notMonthlyAvailable) {
                        rows.push(
                            <ComparableResultsRow
                                breakpoint={breakpoint}
                                id={`feature-filtered-product-row-${i}`}
                                key={`FeatureFiltered-.${result.quote.getIn(['productMeta', 'id'])}`}
                                layout={['component_layoutFeatureFilteredOutResultsRow']}
                                product={result.quote}
                            />
                        );
                    }
                }
            });
            return rows;
        }
        return null;
    };

    /**
     * Render the not available results rows.
     * @param {Immutable.List} brands The unavailable brands list
     * @returns {Array} An array of unavailable result rows
     */
    const renderUnavailableBrands = (brands) => {
        return brands.map((brand) => (
            <ComparableResultsRow
                breakpoint={breakpoint}
                key={`unavailableBrand.${brand.getIn(['providerMeta', 'code'])}`}
                layout={['component_layoutUnavailableResultsRow']}
                product={brand}
            />
        ));
    };

    /**
     * Return the quick price controls if they have been defined
     * @returns {Array} - The quick price controls or null if they have not been defined
     */
    const getControls = () => {
        const context = pageContext.getProps();
        const { models } = context;
        const { componentSchemaModel } = models;
        // some verticals like Energy doesn't render this, so check if the schema exists
        if (componentSchemaModel.componentSchemaExists('layoutResultsControl')) {
            return <ResultsControls shouldAffix={false} layout="component_layoutResultsControl" />;
        }
        return null;
    };

    const hasSavedResults = getHasSavedResults() || getFeatureFlag('prefillHasSavedResults');
    const vertical = pageContext.getVertical();

    return (
        <div id="resultsContainer" className={styles.resultsContainer}>
            {getControls()}
            {renderQuotes(quotes, layout, hasSavedResults, vertical)}
            {renderFilteredOutQuotes(filteredQuotes)}
            {renderFeatureFilteredOutQuotes(filteredQuotes)}
            {renderUnavailableBrands(unavailableBrands)}
        </div>
    );
};

QuickPrice.propTypes = {
    layout: PropTypes.array.isRequired,
    quotes: PropTypes.instanceOf(List).isRequired,
    filteredQuotes: PropTypes.instanceOf(List).isRequired,
    unavailableBrands: PropTypes.instanceOf(List),
    breakpoint: PropTypes.string.isRequired
};

export { QuickPrice };
